/**
 * SelectField Component
 *
 * A styled select/dropdown component with consistent design system styling.
 */

import React from 'react';
import { FormControl, InputLabel, Select, MenuItem, FormHelperText, Box, Typography } from '@mui/material';
import type { SelectChangeEvent, SxProps, Theme } from '@mui/material';
import { colors, typography as typographyTokens, components, radii } from '../../tokens';

export interface SelectOption {
    value: string | number;
    label: string;
    disabled?: boolean;
}

export interface SelectFieldProps {
    /** Field label */
    label: string;
    /** Current value */
    value: string | number;
    /** Available options */
    options: SelectOption[];
    /** Change handler */
    onChange: (value: string | number) => void;
    /** Placeholder text */
    placeholder?: string;
    /** Helper text displayed below the field */
    helperText?: string;
    /** Error state */
    error?: boolean;
    /** Error message */
    errorMessage?: string;
    /** Whether the field is disabled */
    disabled?: boolean;
    /** Whether the field is required */
    required?: boolean;
    /** Field size */
    size?: 'small' | 'medium';
    /** Full width */
    fullWidth?: boolean;
    /** Additional styles */
    sx?: SxProps<Theme>;
}

export const SelectField: React.FC<SelectFieldProps> = ({
    label,
    value,
    options,
    onChange,
    placeholder,
    helperText,
    error = false,
    errorMessage,
    disabled = false,
    required = false,
    size = 'small',
    fullWidth = true,
    sx,
}) => {
    const handleChange = (event: SelectChangeEvent<string | number>) => {
        onChange(event.target.value);
    };

    const displayError = error || Boolean(errorMessage);
    const displayHelperText = errorMessage || helperText;

    return (
        <Box sx={sx}>
            {/* Standalone Label */}
            <Typography
                component='label'
                sx={{
                    display: 'block',
                    mb: '6px',
                    fontWeight: typographyTokens.fontWeight.medium,
                    color: displayError ? colors.error.main : colors.neutral[500],
                    fontSize: typographyTokens.fontSize.sm,
                }}>
                {label}
                {required && <span style={{ color: colors.error.main, marginLeft: 4 }}>*</span>}
            </Typography>

            <FormControl fullWidth={fullWidth} size={size} error={displayError} disabled={disabled}>
                <Select
                    value={value}
                    onChange={handleChange}
                    displayEmpty
                    renderValue={(selected) => {
                        // If value is empty, show the first option's label (typically "All...") in lighter grey
                        if (selected === '' || selected === undefined) {
                            const firstOption = options.find((opt) => opt.value === '');
                            if (firstOption) 
                                return <Typography sx={{ color: colors.neutral[500], fontSize: typographyTokens.fontSize.sm }}>{firstOption.label}</Typography>;
                            if (placeholder)
                                return <Typography sx={{ color: colors.neutral[500], fontSize: typographyTokens.fontSize.sm }}>{placeholder}</Typography>;
                            return <Typography sx={{ color: colors.neutral[500], fontSize: typographyTokens.fontSize.sm }}>{options[0]?.label || ''}</Typography>;
                        }
                        const selectedOption = options.find((opt) => opt.value === selected);
                        return selectedOption?.label || selected;
                    }}
                    sx={{
                        height: 40,
                        '& .MuiSelect-select': {
                            fontSize: typographyTokens.fontSize.sm,
                            color: colors.neutral[800],
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: displayError ? colors.error.main : colors.neutral[300],
                            borderRadius: `${radii.md}px`,
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: displayError ? colors.error.dark : colors.neutral[400],
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: displayError ? colors.error.main : colors.primary[500],
                            borderWidth: 2,
                        },
                    }}>
                    {placeholder && (
                        <MenuItem value='' disabled>
                            <Typography sx={{ color: colors.neutral[400] }}>{placeholder}</Typography>
                        </MenuItem>
                    )}
                    {options.map((option) => (
                        <MenuItem key={option.value} value={option.value} disabled={option.disabled}>
                            {option.label}
                        </MenuItem>
                    ))}
                </Select>

                {displayHelperText && (
                    <FormHelperText
                        sx={{
                            color: displayError ? colors.error.main : colors.neutral[500],
                            fontSize: typographyTokens.fontSize.xs,
                            mt: 0.5,
                        }}>
                        {displayHelperText}
                    </FormHelperText>
                )}
            </FormControl>
        </Box>
    );
};

export default SelectField;
